<template>
  <v-dialog v-model="dialogVisible" max-width="800">
    <v-card>
      <div class="card-content-wrapper">
        <!-- Sidebar з табами -->
        <div class="sidebar">
          <v-card-title class="card-header">
            <span class="headline">{{ activeTab.name | translate }}</span>
          </v-card-title>
          <div class="sidebar-title">{{ 'color_indication.section_label' | translate }}</div>
          <v-tabs 
            vertical 
            :options="tabs" 
            :value="activeTab" 
            @input="activeTab = $event">
          </v-tabs>
        </div>

        <div class="content">
          <color-indication-tab
            v-show="activeTab.key === 'color_indication'"
            ref="colorIndicationTab"
            :loading="loading"
            :settings="settings"
            :readonly="readonly"
            @save="handleAutoSave"
          />

          <loading-errors-tab
            v-show="activeTab.key === 'loading_errors'"
            :active="activeTab.key === 'loading_errors'"
            :readonly="readonly"
          />

          <unloading-errors-tab
            v-show="activeTab.key === 'unloading_errors'"
            :active="activeTab.key === 'unloading_errors'"
            :readonly="readonly"
          />
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api'
import ColorIndicationTab from './tabs/ColorIndicationTab.vue'
import LoadingErrorsTab from './tabs/LoadingErrorsTab.vue'
import UnloadingErrorsTab from './tabs/UnloadingErrorsTab.vue'

export default {
  name: 'ColorIndicationDialog',

  components: {
    ColorIndicationTab,
    LoadingErrorsTab,
    UnloadingErrorsTab
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      activeTab: { id: 0, name: 'color_indication.tabs.color_indication', key: 'color_indication' },
      tabs: [
        { id: 0, name: 'color_indication.tabs.color_indication', key: 'color_indication' },
        { id: 1, name: 'color_indication.tabs.loading_errors', key: 'loading_errors' },
        { id: 2, name: 'color_indication.tabs.unloading_errors', key: 'unloading_errors' }
      ],
      loading: false,
      settings: {
        leftovers: {
          enabled: false,
          green: 0,
          yellow: 0,
          red: 0,
          min_value: 0,
          max_value: 100,
          direction: 'less_is_better',
          value_type: 'float'
        },
        load_weight: {
          enabled: false,
          green: 0,
          yellow: 0,
          red: 0,
          min_value: 0,
          max_value: 100,
          direction: 'less_is_better',
          value_type: 'float'
        },
        upload_weight: {
          enabled: false,
          green: 0,
          yellow: 0,
          red: 0,
          min_value: 0,
          max_value: 100,
          direction: 'less_is_better',
          value_type: 'float'
        },
        daily_intake_left: {
          enabled: false,
          green: 0,
          yellow: 0,
          red: 0,
          min_value: 0,
          max_value: 999,
          direction: 'more_is_better',
          value_type: 'integer'
        }
      }
    }
  },

  computed: {
    dialogVisible: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
        if (!val) {
          this.$emit('close')
        }
      }
    }
  },

  watch: {
    dialogVisible (newVal) {
      if (newVal) {
        this.activeTab = this.tabs[0]
        this.loadSettings()
      }
    }
  },

  methods: {
    closeDialog () {
      this.dialogVisible = false
    },

    async loadSettings () {
      this.loading = true
      const farmId = this.$route.params.farm_id
      try {
        const { data } = await api.monitoring.farms(farmId)
        if (data) {
          Object.keys(this.settings).forEach(key => {
            if (data[key]) {
              this.settings[key] = {
                enabled: data[key].enabled || false,
                green: data[key].green || 0,
                yellow: data[key].yellow || 0,
                red: data[key].red || 0,
                min_value: data[key].min_value !== undefined ? data[key].min_value : this.settings[key].min_value,
                max_value: data[key].max_value !== undefined ? data[key].max_value : this.settings[key].max_value,
                direction: data[key].direction || this.settings[key].direction,
                value_type: data[key].value_type || this.settings[key].value_type
              }
            }
          })

          this.$nextTick(() => {
            if (this.$refs.colorIndicationTab) {
              this.$refs.colorIndicationTab.validateAllFields()
            }
          })
        }
      } catch (error) {
        console.error('Error loading monitoring settings:', error)
        this.$toasted.error(this.$options.filters.translate('color_indication.messages.load_error'))
      } finally {
        this.loading = false
      }
    },

    async handleAutoSave (settings) {
      const farmId = this.$route.params.farm_id

      try {
        const payload = {}

        Object.keys(settings).forEach(key => {
          payload[key] = {
            enabled: settings[key].enabled,
            green: Number(settings[key].green),
            yellow: Number(settings[key].yellow),
            red: Number(settings[key].red)
          }
        })

        await api.monitoring.updateFarms(farmId, payload)

        this.$toasted.success(this.$options.filters.translate('color_indication.messages.settings_saved'), {
          duration: 2000,
          position: 'bottom-right'
        })
      } catch (error) {
        console.error('❌ Error auto-saving settings:', error)
        this.$toasted.error(this.$options.filters.translate('color_indication.messages.save_error'))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.v-card__title {
  padding-top: 14px;
  padding-right: 10px;
  padding-left: 20px;
  padding-bottom: 18px;
  border-bottom: 1px solid #D7D7D7;
  margin-bottom: 16px;
}

.v-tabs {
  flex: 1;
  margin-top: 8px;
  padding: 0 10px;
}

.v-tabs--vertical .v-tab--active {
  font-size: 1.4rem;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #D7D7D7;
}

.headline {
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  white-space: nowrap;
}

.card-content-wrapper {
  display: flex;
  min-height: 610px;
}

.sidebar {
  width: 240px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #D7D7D7;
}

.sidebar-title {
  padding: 0 10px;
  font-size: 14px;
  color: #979797;
}

.content {
  flex: 1;
  margin-top: 56px;
  border-top: 1px solid #D7D7D7;
  padding-top: 16px;
  padding-left: 20px;
  padding-bottom: 20px;
  padding-right: 10px;
  height: 600px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #59cc56 rgba(0, 0, 0, 0);

  &::-webkit-scrollbar {
    width: 0.6rem;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #59CC56;
    border-radius: 0.3rem;
    border: 0.1rem solid transparent;
  }

  &:hover::-webkit-scrollbar-thumb {
    background-color: #4ab83f;
  }
}
</style>